import { sendAnalyticsEvent } from '@faststore/sdk'
import { ChatFooterIcon } from 'src/components/icons/ChatFooterIcon'
import { TelephoneIcon } from 'src/components/icons/TelephoneIcon'
import androidBadge from 'src/images/android-badge.png'
import appleBadge from 'src/images/apple-badge.png'
import awardIcon from 'src/images/awardIcon.png'
import vetIcon from 'src/images/vetIcon.png'
import { FooterClick } from 'src/sdk/analytics/types'

type ContactInfoProp = {
  contactInfo: ContactInfo
}

type ContactInfo = {
  title?: string
  telephoneSectionTitle?: string
  telephoneNumber?: string
  secondAddressTitle?: string
  secondAddressValue?: string
  secondAddressUrl?: string
  businessDays?: string
  businessHours?: string
  googleLink?: string
  appleLink?: string
  socialLogos?: SocialLogo[]
}

type SocialLogo = {
  link?: string
  logoImage?: string
}

export const ContactInfo = ({ contactInfo }: ContactInfoProp) => {
  const handleSupportClick = (subCategory: string) => {
    if (!subCategory) return
    sendAnalyticsEvent<FooterClick>({
      name: 'footer',
      params: {
        click_area: 'Assistenza clienti',
        link_clicked: subCategory,
      },
    })
  }

  const getSocialName = (social?: string) => {
    if (!social) return 'Social logo'

    if (social.includes('facebook')) {
      return 'Facebook'
    } else if (social.includes('instagram')) {
      return 'Instagram'
    } else if (social.includes('x.com') || social.includes('twitter')) {
      return 'X'
    } else if (social.includes('tiktok')) {
      return 'Tiktok'
    } else if (social.includes('youtube')) {
      return 'YouTube'
    }
    return 'Social logo'
  }

  if (!contactInfo) return null

  return (
    <div className="mt-6 lg:mt-0 px-6 md:px-10 lg:px-4 2xl:px-6 py-6 lg:py-8 text-black flex-1 lg:flex-[0.24] 2xl:flex-[0.20] bg-yellow">
      <div className="flex flex-col gap-6 flex-nowrap">
        <h4 className="flex text-black py-1 gap-[10px] justify-between w-full">
          <span className="block w-full text-base xs:text-lg font-semibold !leading-[120%] tracking-[0.08px] xs:tracking-[0.024px] text-left  lg:text-sm lg-tracking-[0.035px]">
            {contactInfo.title}
          </span>
        </h4>
        <div className="flex flex-col gap-3 flex-nowrap">
          {contactInfo.telephoneSectionTitle && contactInfo.telephoneNumber && (
            <div className="flex flex-row gap-1 flex-nowrap items-center">
              <span className="block w-8 h-8">
                <TelephoneIcon />
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="block text-sm teleph!leading-[120%] tracking-[0.035px] font-normal  xs:text-base xs:tracking-[0.08px] lg:text-xs lg:tracking-[0.048px]">
                  {contactInfo.telephoneSectionTitle}
                </span>
                <a
                  onClick={() => handleSupportClick('Servizio clienti')}
                  className="block text-sm !leading-[120%] tracking-[0.035px] font-semibold  xs:text-base xs:tracking-[0.08px] lg:text-xs lg:tracking-[0.048px]"
                  href={`tel:${contactInfo.telephoneNumber}`}
                >
                  {contactInfo.telephoneNumber}
                </a>
              </div>
            </div>
          )}
          {contactInfo.secondAddressTitle && contactInfo.secondAddressUrl && (
            <div className="flex flex-row gap-1 flex-nowrap items-center">
              <span className="block w-8 h-8">
                <ChatFooterIcon />
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="block text-sm !leading-[120%] tracking-[0.035px] font-normal  xs:text-base xs:tracking-[0.08px] lg:text-xs lg:tracking-[0.048px]">
                  {contactInfo.secondAddressTitle}
                </span>
                {contactInfo.secondAddressUrl ? (
                  <a
                    href={contactInfo.secondAddressUrl}
                    target="_blank"
                    className="block text-sm !leading-[120%] tracking-[0.035px] font-semibold  xs:text-base xs:tracking-[0.08px] lg:text-xs lg:tracking-[0.048px]"
                    onClick={() => handleSupportClick('Servizio clienti')}
                  >
                    {contactInfo.secondAddressValue}
                  </a>
                ) : (
                  <span
                    onClick={() => handleSupportClick('Servizio clienti')}
                    className="block text-sm !leading-[120%] tracking-[0.035px] font-semibold  xs:text-base xs:tracking-[0.08px] lg:text-xs lg:tracking-[0.048px]"
                  >
                    {contactInfo.secondAddressValue}
                  </span>
                )}
              </div>
            </div>
          )}
          {contactInfo.businessDays && contactInfo.businessHours && (
            <div className="space-y-0.5">
              <span className="block text-xs !leading-[120%] tracking-[0.048px] font-normal  xs:text-sm xs:tracking-[0.035px] lg:text-[10px] lg:tracking-[0.15px]">
                {contactInfo.businessDays}
              </span>
              <span className="block text-xs !leading-[120%] tracking-[0.048px] font-semibold  xs:text-sm xs:tracking-[0.035px] lg:text-[10px] lg:tracking-[0.15px]">
                {contactInfo.businessHours}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 flex-nowrap">
          <h4 className="flex text-black py-1 gap-[10px] justify-between w-full">
            <span className="block w-full text-base xs:text-lg font-semibold !leading-[120%] tracking-[0.08px] xs:tracking-[0.024px] text-left  lg:text-sm lg-tracking-[0.035px]">
              Scarica la nostra App
            </span>
          </h4>
          <div className="flex flex-row gap-3 flex-nowrap">
            <a
              onClick={() => handleSupportClick('Android app')}
              aria-label="Google Play Store"
              href={contactInfo.googleLink}
            >
              <img
                className="block max-h-10 lg:max-h-[30px]"
                alt="Google Play Store Icon"
                src={androidBadge}
              />
            </a>
            <a
              onClick={() => handleSupportClick('iOS app')}
              href={contactInfo.appleLink}
              aria-label="iOS App Store"
            >
              <img
                className="block max-h-10 lg:max-h-[30px]"
                alt="Apple Store Icon"
                src={appleBadge}
              />
            </a>
          </div>
        </div>
        <div className="flex flex-col gap-4 flex-nowrap">
          <h4 className="flex text-black py-1 gap-[10px] justify-between w-full">
            <span className="block w-full text-base xs:text-lg font-semibold !leading-[120%] tracking-[0.08px] xs:tracking-[0.024px] text-left  lg:text-sm lg-tracking-[0.035px]">
              Seguici su:
            </span>
          </h4>
          <div className="flex flex-row flex-nowrap gap-4 lg:gap-2.5">
            {contactInfo.socialLogos?.map((el: SocialLogo, index: number) => (
              <a
                key={`contact-info-sec-${index}`}
                onClick={() => handleSupportClick(getSocialName(el.link))}
                href={el.link}
                target="_blank"
                aria-label={getSocialName(el.link) ?? 'Icona social'}
                className="block w-10 h-10 lg:w-8 lg:h-8 p-[5px] lg:p-1 rounded-full border border-solid border-[#FFF399]"
              >
                <span
                  className="relative w-full h-full block bg-contain bg-no-repeat bg-center"
                  style={{ backgroundImage: `url(${el.logoImage})` }}
                ></span>
              </a>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <a
              target="_blank"
              href="https://www.salute.gov.it/LogoCommercioElettronicoVET/CercaSitoEComm?useMode=getDettaglio&lingua=italiano&id=5907&area=veterinari&menu=vendite&VOLID=XCEC0000514"
            >
              <img src={vetIcon} alt="VET-icon" />
            </a>
            <a
              target="_blank"
              href="https://arcaplanetgroup.com/arcaplanet-e-la-prima-insegna-in-assoluto-per-clientela-soddisfatta/"
            >
              <img className="h-[75px]" src={awardIcon} alt="award-icon" />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
